import { z } from "zod";
import { RUN_PREFLIGHT_VERDICTS } from "./api/preflight.js";
import { DeliverableSchema } from "./catalog-sidecar.js";
import { OutcomeCriterionSchema } from "./outcome.js";
import { ENGINE_TARGETS, RUNTIME_PROVIDERS } from "./runtime.js";
import { CatalogTemplateSchema, LibraryAttributionSchema, TemplateSourceSchema } from "./wire.js";
// What a front-end can rely on about one template (V6 plan 13.3.3, the read API table; H.28):
// the detail, the needs the publish derived for it per provider and runner, the live passes
// the owner's sweeps recorded for it, and its versions. Wire 5.20.0.
const SHA256_HEX = /^[a-f0-9]{64}$/;
/**
 * What a template needs on one provider and runner (catalog_entries.needs, H.9): the checks a
 * run start makes, run by the publish over the template's documents with its models on that
 * provider. `verdict` is blocked when a start would refuse, degraded when it runs with
 * warnings, ok otherwise, and null when nothing was checked (Foundry's pairs, held, and pairs
 * the release does not route). `blockers` and `warnings` are rule ids only, never text.
 * `served` says whether this deployment serves the pair now: the publish's answer, read
 * against the engines the server has installed. A start still re-checks.
 */
export const CatalogNeedSchema = z.object({
    provider: z.enum(RUNTIME_PROVIDERS),
    runner: z.enum(ENGINE_TARGETS),
    verdict: z.enum(RUN_PREFLIGHT_VERDICTS).nullable(),
    blockers: z.array(z.string()),
    warnings: z.array(z.string()),
    served: z.boolean(),
    /** When the publish derived these answers (a later publish that finds the same ones keeps it). */
    checkedAt: z.string().datetime(),
});
/**
 * One live pass the owner's sweeps recorded (content/tested-ledger.json, H311): on which
 * provider and runner, whether it passed, on which day, what it cost, and the template's content
 * checksum it ran on. Verdicts, costs, dates and checksums only: never a run's frames.
 */
export const CatalogTestedSchema = z
    .object({
    provider: z.enum(RUNTIME_PROVIDERS),
    runner: z.enum(ENGINE_TARGETS),
    verdict: z.enum(["pass", "fail"]),
    /** The day the pass ran (YYYY-MM-DD). */
    at: z.string().date(),
    costCents: z.number().int().min(0),
    /** The template version's content checksum the pass ran on (catalog.versions `checksum`). */
    checksum: z.string().regex(SHA256_HEX),
})
    .strict();
/** The tested ledger's file, beside taxonomy.yaml at the content root. */
export const TESTED_LEDGER_FILE = "tested-ledger.json";
/** A ledger entry: a pass and the template it ran on, by `<library_key>/<slug>`. */
export const TestedLedgerEntrySchema = CatalogTestedSchema.extend({
    ref: z
        .string()
        .max(161)
        .regex(/^[a-z0-9]+(?:[_-][a-z0-9]+)*\/[a-z0-9]+(?:[_-][a-z0-9]+)*$/),
}).strict();
/**
 * content/tested-ledger.json: every live pass the owner's sweeps recorded, written on the Mac
 * (sweeps stay there) and committed by content PRs. Strict at every level, so nothing but
 * these fields can ride in it. It may be empty.
 */
export const TestedLedgerSchema = z
    .object({ entries: z.array(TestedLedgerEntrySchema).max(10_000) })
    .strict();
/** A starter file the template ships (`files/<name>`), which a project created from it uploads. */
export const CatalogStarterFileSchema = z.object({
    path: z.string().min(1),
    mediaType: z.string().min(1),
    sizeBytes: z.number().int().min(0),
});
/** The outcome a run of the template is graded against (catalog.yaml `outcome`). */
export const CatalogDetailOutcomeSchema = z.object({
    summary: z.string(),
    criteria: z.array(OutcomeCriterionSchema),
    maxIterations: z.number().int().min(1),
    deliverables: z.array(DeliverableSchema),
});
/** The library the template sits in, its name and description in the request locale. */
export const CatalogDetailLibrarySchema = z.object({
    key: z.string().min(1),
    name: z.string().min(1),
    description: z.string(),
    attribution: LibraryAttributionSchema.nullable(),
});
/**
 * Canonical template detail (catalog.detail by `{libraryKey, slug}`, the live version, or by
 * `{templateVersionId}`, any version of a live template): the prompt-free catalog row plus what
 * a front-end shows before a project exists (wire 5.20.0, H.28). For a version that is not the
 * live one, the row is that version's own (its title, category, tags, industries, kind, members
 * and capabilities), `memberOf` is the template's, and `needs` is empty: the publish derives
 * needs for the live version only. Each added field is defaulted or optional, so a payload from
 * an older server still parses.
 */
export const TemplateDetailSchema = CatalogTemplateSchema.extend({
    /** This version is the one the catalog lists (its template's live version). */
    live: z.boolean().default(true),
    /** The first message a run sends when the person names none (catalog.yaml `goal`). */
    goal: z.string().nullable().default(null),
    /** What a run is graded against; null when the template defines no outcome. */
    outcome: CatalogDetailOutcomeSchema.nullable().default(null),
    /** The files a project created from this version starts with, by path. */
    starterFiles: z.array(CatalogStarterFileSchema).default([]),
    /** One need per provider and runner (RUNTIME_PROVIDERS × ENGINE_TARGETS, in that order). */
    needs: z.array(CatalogNeedSchema).default([]),
    /** The live passes recorded on this version's content checksum, newest first; [] when none. */
    tested: z.array(CatalogTestedSchema).default([]),
    library: CatalogDetailLibrarySchema.nullable().default(null),
    /**
     * The upstream this version copied from when it is not its library's (the template's
     * `SOURCE.json`, H.26): `{repo, commit, license, holder, paths, change}`. Absent when the
     * template names none; the library's own attribution is `library.attribution`.
     */
    source: TemplateSourceSchema.optional(),
});
/** One version of a template (catalog.versions), newest first. */
export const CatalogVersionSchema = z.object({
    templateVersionId: z.string().min(1),
    version: z.number().int().positive(),
    createdAt: z.string().datetime(),
    /** The version the catalog lists now. */
    live: z.boolean(),
    /** Its content checksum: what a tested-ledger entry names. */
    checksum: z.string().regex(SHA256_HEX),
});
//# sourceMappingURL=catalog-detail.js.map